import {
  NavigationItemAlignment,
  NavigationItemDirection,
  NavigationItemIconSize,
  NavigationPosition,
  NavigationResolution,
} from '@unifyapps/defs/types/interface';
import { cva } from 'class-variance-authority';
import { EXPANDED_PLATFORM_SIDE_NAV_WIDTH } from './components/platform/constants';
import { EXPANDED_SIDE_NAV_WIDTH } from './constants';
import {
  ExpandableNavigationVariant,
  NavigationItemLabelVariantEnum,
  NavigationItemSelectionVariantEnum,
} from './types';

export const navItemLinkRootVariants = cva('flex items-center outline-offset-0 gap-y-sm', {
  variants: {
    variant: {
      lite: 'h-9 ps-[9px] pe-md !gap-x-md rounded-md',
      rich: '',
    },
    active: {
      true: 'focus-visible:ring-brand',
      false: 'focus-visible:ring-gray',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: 'gap-x-md',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    expandableNavVariant: {
      [ExpandableNavigationVariant.XS]: '',
      [ExpandableNavigationVariant.SM]: '',
    },
    expanded: {
      true: '',
      false: '',
    },
    position: {
      [NavigationPosition.Start]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Top]: '',
      [NavigationPosition.End]: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: 'justify-center',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: 'justify-center',
      [NavigationItemLabelVariantEnum.None]: 'justify-center',
      [NavigationItemLabelVariantEnum.Platform]: '!gap-x-lg rounded-md ',
    },
    resolution: {
      [NavigationResolution.XL]: '',
      [NavigationResolution.LG]: 'disable-hover',
      [NavigationResolution.MD]: 'disable-hover',
      [NavigationResolution.SM]: 'disable-hover touch-none select-none',
    },
    direction: {
      [NavigationItemDirection.Column]: '',
      [NavigationItemDirection.Row]: '',
    },
    alignment: {
      [NavigationItemAlignment.Center]: '',
      [NavigationItemAlignment.Start]: '',
    },
  },
  compoundVariants: [
    // Lite variant styling (common for both types)
    {
      variant: 'lite',
      active: true,
      className: 'ua-side-nav-item-lite-active',
    },
    {
      variant: 'lite',
      active: false,
      className: 'ua-side-nav-item-lite-active-false',
    },
    // rich-variant styling. Not Applying 'ua-side-nav-item-active-false'
    // if solid expandable variant
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      position: NavigationPosition.Top,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Bottom,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active-false',
    },
    // For solid expandable variant , special class
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-solid-expandable-item-active-false',
    },
    // Whenever navItem is hovered, to change icon and text color.
    // Rich only: this marker exists purely to re-colour the label and icon on hover, and the lite
    // rail holds one colour in every state.
    {
      variant: 'rich',
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'group/NavItemLinkDefault',
    },
    {
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'group/NavSolidExpandableItemLinkDefault',
    },
    // Stroke-specific styles when Navbar width 56px(SM variant)
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[18px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'group/NavItemLinkActive ps-[15px] border-s-[3px]',
    },
    // Stroke-specific styles when Navbar width 52px(XS variant)
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'group/NavItemLinkActive ps-[13px] border-s-[3px]',
    },
    // Rich variant styling. Dont apply 'ua-side-nav-item-active' for
    // solid expandable
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Top,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Bottom,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-item-active',
    },
    // For solid expandable variant , special class
    {
      variant: 'rich',
      active: true,
      expanded: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-solid-expandable-active',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'border-s-[3px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'border-s-[3px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'border-s-[3px]',
    },
    // Solid-specific styles. Padding to adjust hover-styles
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      variant: 'rich',
      expanded: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'pe-md',
    },
    // Stroke-specific styles when position is bottom
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      className: 'group/NavItemLinkActive border-t-[3px]',
    },
    // To center align the expandable nav item when expandableNavVariant is XS i.e 52px navbar width
    {
      expanded: false,
      active: false,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[18px]',
    },
    {
      expanded: false,
      active: true,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[15px]',
    },
    {
      expanded: true,
      active: true,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[15px]',
    },
    {
      expanded: true,
      active: false,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[18px]',
    },
    // To center align the nav item
    {
      position: NavigationPosition.Bottom,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'flex-1 justify-center h-full',
    },
    // Solid-specific styles.Margin given to control
    // hover styles
    {
      expanded: false,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      className: 'ms-[10px] me-md rounded-md',
    },
    {
      expanded: true,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      className: 'ms-[10px] me-md rounded-md',
    },
    // Stroke-specific padding
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      className: 'py-md',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'px-[9px] py-[9px]',
    },
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'pe-md',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'my-xs',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'my-xs',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'my-xs',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'my-xxs',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Column,
      className: 'flex-col',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Column,
      className: 'flex-col',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Row,
      className: 'flex-row',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      direction: NavigationItemDirection.Row,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      alignment: NavigationItemAlignment.Start,
      className: 'flex-start',
    },
    {
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ps-[9px] pe-[9px] py-[9px]',
    },
    {
      expanded: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      // pe 8 (not 9): the trailing chevron is a full 16px box, so unlike the leading glyph it
      // takes the Figma box-level 8px inset from the item edge.
      className: 'py-[8px] pe-[8px]',
    },
    // {
    //   variant: 'rich',
    //   position: NavigationPosition.Start,
    //   direction: NavigationItemDirection.Row,
    //   alignment: NavigationItemAlignment.Center,
    //   labelVariant: NavigationItemLabelVariantEnum.Upfront,
    //   // className: 'justify-center', //todo Use this
    //   className: 'flex-start',
    // },
    {
      variant: 'rich',
      resolution: NavigationResolution.SM,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'flex-col',
    },
  ],
  defaultVariants: {
    resolution: NavigationResolution.XL,
    expandableNavVariant: ExpandableNavigationVariant.SM,
  },
});

// when parent has icon we want to make the size bigger, otherwise smaller
export const navItemLinkIconVariants = cva('group-[.has-no-icon]:size-3', {
  variants: {
    disabled: {
      true: '!text-icon-disabled',
      false: '',
    },
    active: {
      true: '',
      false: '',
    },
    variant: {
      lite: 'group-[.has-icon]:size-3.5',
      rich: '',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    resolution: {
      [NavigationResolution.LG]: 'disable-hover',
      [NavigationResolution.MD]: 'disable-hover',
      [NavigationResolution.SM]: 'disable-hover',
      [NavigationResolution.XL]: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: '',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: '',
      [NavigationItemLabelVariantEnum.None]: '',
      [NavigationItemLabelVariantEnum.Platform]: '',
    },
    position: {
      [NavigationPosition.Start]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Top]: '',
      [NavigationPosition.End]: '',
    },
    size: {
      [NavigationItemIconSize.XS]: '',
      [NavigationItemIconSize.SM]: '',
      [NavigationItemIconSize.MD]: '',
      [NavigationItemIconSize.LG]: '',
    },
    isSubNavItem: {
      true: '',
      false: '',
    },
    isHorizontalUpfront: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      isHorizontalUpfront: false,
      className: 'me-md',
    },
    {
      variant: 'rich',
      active: true,
      // Since we do not render icons for sub-navigation items, no need to
      // apply this class to prevent active styles getting applied
      isSubNavItem: false,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: false,
      // Since we do not render icons for sub-navigation items, no need to
      // apply this class to prevent active styles getting applied
      isSubNavItem: false,
      className: 'ua-side-nav-item-active-false',
    },
    {
      // one class for both states: the lite rail marks selection with a background alone, so its
      // icons hold one colour selected or not.
      variant: 'lite',
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      size: NavigationItemIconSize.XS,
      className: 'group-[.has-icon]:size-3',
    },
    {
      variant: 'rich',
      size: NavigationItemIconSize.SM,
      className: 'group-[.has-icon]:size-4',
    },
    {
      variant: 'rich',
      size: NavigationItemIconSize.MD,
      className: 'group-[.has-icon]:size-5',
    },
    {
      variant: 'rich',
      size: NavigationItemIconSize.LG,
      className: 'group-[.has-icon]:size-6',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'group-[.has-icon]:size-[18px]',
    },
    // rich-variant styling. When navitem is active and selectionVariant is stroke,
    // due to 3px border-left, label shifts. To prevent this , adding this translate class
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px]',
    },
  ],
  defaultVariants: {
    selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
    size: NavigationItemIconSize.MD,
    isSubNavItem: false,
  },
});

export const navItemLinkLabelVariants = cva('text-sm truncate transition-all', {
  variants: {
    disabled: {
      true: '!text-disabled',
      false: '',
    },
    active: {
      true: '',
      false: '',
    },
    expanded: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
    variant: {
      lite: 'font-medium',
      rich: 'font-semibold ',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: 'text-xxxs opacity-100 font-medium',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: '',
      [NavigationItemLabelVariantEnum.None]: '',
      // platform labels are Text sm/Regular in Figma; overrides the rich-variant font-semibold
      [NavigationItemLabelVariantEnum.Platform]: 'font-normal',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Solid]: '',
      [NavigationItemSelectionVariantEnum.Stroke]: '',
    },
    position: {
      [NavigationPosition.Start]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Top]: '',
      [NavigationPosition.End]: '',
    },
    resolution: {
      [NavigationResolution.LG]: 'disable-hover',
      [NavigationResolution.MD]: 'disable-hover',
      // In mobile variant, we want label to be limited to 60px
      [NavigationResolution.SM]: 'max-w-15 disable-hover whitespace-normal',
      [NavigationResolution.XL]: '',
    },
    isPlatformSideNav: {
      true: 'font-medium',
      false: '',
    },
  },
  compoundVariants: [
    // Lite-specific styles.
    //
    // Selection on the lite rail is a background and nothing else, so the label reads the same
    // whether the row is selected or not — hence one class for both states rather than a pair.
    // The label now sets its colour directly (`text-primary`) instead of carrying
    // `ua-side-nav-item-active-false`, so the shared `span.ua-side-nav-item-active-false:hover`
    // re-colour can no longer match it; `disable-hover` is kept as belt-and-braces against the other
    // `:not(.disable-hover):hover` rules in sideNavigation.css.
    {
      variant: 'lite',
      className: 'text-primary disable-hover',
    },
    {
      variant: 'lite',
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'font-normal',
    },
    // Appling 'ua-side-nav-item-active-false' to all navItem variant
    // except solid expandable
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active-false',
    },
    // Special class for solid expandable
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-solid-expandable-item-active-false',
    },
    // rich-variant styling. When navitem is active and selectionVariant is stroke,
    // due to 3px border-left, label shifts. To prevent this , adding this translate class
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px] duration-0',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px] duration-0',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px] duration-0',
    },
    // rich-variant styling. When navitem is active and selectionVariant is stroke,
    // due to 3px border-top, label shifts. To prevent this , adding this translate class
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px] duration-0',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px] duration-0',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px] duration-0',
    },
    // Applying 'ua-side-nav-item-active' class except for
    // solid expandable variant
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-item-active',
    },
    // Special-class for solid-expandable
    {
      variant: 'rich',
      active: true,
      expanded: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-solid-expandable-active',
    },
  ],
});

export const groupNavItemLinkLabelVariants = cva('text-sm font-medium', {
  variants: {
    active: {
      true: '',
      false: 'ua-side-nav-nested-item-active-false',
    },
    expanded: {
      true: '',
      false: '',
    },
    variant: {
      lite: '',
      rich: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: '',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: '',
      [NavigationItemLabelVariantEnum.None]: '',
      [NavigationItemLabelVariantEnum.Platform]: '',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
  },
  compoundVariants: [
    {
      // both states resolve to the same classes: selection on the lite rail is a background alone, so
      // a nested label reads the same selected or not. The pair only exists because `active: false`
      // already contributes the colour class above — the selected side has to add it back.
      variant: 'lite',
      active: true,
      className: 'ua-side-nav-nested-item-active-false !font-normal',
    },
    {
      variant: 'lite',
      active: false,
      className: '!font-normal',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'ua-side-nav-nested-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'ua-side-nav-nested-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'ua-side-nav-nested-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-nested-item-active',
    },
    {
      variant: 'rich',
      active: true,
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-nested-item-active',
    },
    {
      variant: 'rich',
      active: true,
      expanded: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-solid-nested-active',
    },
  ],
});

export const groupNavItemLinkVariants = cva(
  'flex w-full flex-row items-center pe-lg py-md leading-5',
  {
    variants: {
      // the `group/NestedNavItemLink*` markers are rich-only (see the compounds): they exist solely to
      // re-colour the label on hover, which the lite rail does not do.
      active: {
        true: 'focus-visible:ring-brand',
        false: 'focus-visible:ring-gray',
      },
      // both lite consumers run this through slowCn, so twMerge resolves these against the shared
      // base (w-full / pe-lg / py-md) by order — no `!` needed to win.
      variant: {
        lite: 'w-auto ps-sm py-none h-7 my-xxs rounded-md',
        rich: 'ps-[18px]',
      },
      // lite only: third-level rows sit inside LiteNestedSubItem's own indent rule, so they drop the
      // second level's inset and end on the tighter trailing padding. See the compounds below.
      isNested: {
        true: '',
        false: '',
      },
      resolution: {
        [NavigationResolution.XL]: '',
        [NavigationResolution.LG]: 'disable-hover',
        [NavigationResolution.MD]: 'disable-hover',
        [NavigationResolution.SM]: 'disable-hover',
      },
      selectionVariant: {
        [NavigationItemSelectionVariantEnum.Stroke]: '',
        [NavigationItemSelectionVariantEnum.Solid]: '',
      },
      position: {
        [NavigationPosition.Start]: '',
        [NavigationPosition.Bottom]: '',
        [NavigationPosition.Top]: '',
        [NavigationPosition.End]: '',
      },
      labelVariant: {
        [NavigationItemLabelVariantEnum.Upfront]: '',
        [NavigationItemLabelVariantEnum.Expandable]: '',
        [NavigationItemLabelVariantEnum.Tooltip]: '',
        [NavigationItemLabelVariantEnum.None]: '',
        [NavigationItemLabelVariantEnum.Platform]: '',
      },
      expanded: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // The lite rail marks selection with a background and nothing else — the same fill its
      // top-level rows carry, so every level reads the same. Exactly one of these lands at a time,
      // which is what keeps the selected fill from being overridden: the transparent rule and the
      // selected one sit in that order in sideNavigation.css, so a row carrying both would lose.
      { variant: 'lite', active: true, className: 'ua-side-nav-item-lite-active' },
      { variant: 'lite', active: false, className: 'ua-side-nav-item-lite-active-false' },
      { variant: 'lite', isNested: false, className: 'ms-[29px] pe-md' },
      { variant: 'lite', isNested: true, className: 'ms-none pe-sm transition-colors' },
      // rich keeps the hover re-colour markers the lite rail deliberately drops.
      { variant: 'rich', active: true, className: 'group/NestedNavItemLinkActive' },
      { variant: 'rich', active: false, className: 'group/NestedNavItemLinkDefault' },
      {
        variant: 'rich',
        labelVariant: NavigationItemLabelVariantEnum.None,
        className: 'gap-x-md',
      },
      {
        variant: 'rich',
        labelVariant: NavigationItemLabelVariantEnum.Upfront,
        className: 'gap-x-md',
      },
      {
        variant: 'rich',
        labelVariant: NavigationItemLabelVariantEnum.Tooltip,
        className: 'gap-x-md',
      },
      {
        variant: 'rich',
        labelVariant: NavigationItemLabelVariantEnum.Expandable,
        selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
        className: 'gap-x-md',
      },
      {
        variant: 'rich',
        active: true,
        selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
        className: 'ua-side-nav-nested-item-active',
      },
      {
        variant: 'rich',
        active: false,
        selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
        className: 'ua-side-nav-nested-item-active-false',
      },
      {
        variant: 'rich',
        active: true,
        position: NavigationPosition.Start,
        selectionVariant: NavigationItemSelectionVariantEnum.Solid,
        className: 'ms-[10px] ps-md rounded-md me-md',
      },
      {
        variant: 'rich',
        active: false,
        position: NavigationPosition.Start,
        selectionVariant: NavigationItemSelectionVariantEnum.Solid,
        className: 'ua-side-nav-nested-item-active-false ms-[10px] ps-md rounded-md me-md',
      },
      // Special class for nested solid items
      {
        labelVariant: NavigationItemLabelVariantEnum.Expandable,
        selectionVariant: NavigationItemSelectionVariantEnum.Solid,
        active: true,
        expanded: true,
        className: 'ua-side-nav-solid-nested-active',
      },
    ],
    defaultVariants: {
      resolution: NavigationResolution.XL,
      isNested: false,
    },
  },
);

export const groupNavItemLinkChevronVariants = cva('size-3 transform flex-shrink-0', {
  variants: {
    active: {
      true: '',
      false: 'ua-side-nav-item-active-false',
    },
    open: {
      true: 'rotate-90',
      false: '',
    },
    expanded: {
      true: '',
      false: 'hidden',
    },
    variant: {
      lite: '!size-4 !text-icon-secondary',
      rich: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: '',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: '',
      [NavigationItemLabelVariantEnum.None]: '',
      [NavigationItemLabelVariantEnum.Platform]: '',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    position: {
      [NavigationPosition.Start]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Top]: '',
      [NavigationPosition.End]: '',
    },
  },
  compoundVariants: [
    // Applying 'ua-side-nav-item-active' class except for
    // solid expandable variant
    {
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'ua-side-nav-item-active',
    },
    {
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'ua-side-nav-item-active',
    },
    {
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'ua-side-nav-item-active',
    },
    {
      active: true,
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ua-side-nav-item-active',
    },
    {
      active: true,
      expanded: true,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ua-side-nav-item-active',
    },
    // Special-class for solid expandable navitem
    {
      active: true,
      expanded: true,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ua-side-nav-solid-expandable-active',
    },
  ],
});

export const buttonNavItemRootVariants = cva('flex', {
  variants: {
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    active: {
      true: 'focus-visible:ring-brand',
      false: 'focus-visible:ring-gray',
    },
    variant: {
      lite: 'w-full h-9 !gap-x-md ps-[9px] pe-md rounded-md',
      rich: '',
    },
    position: {
      [NavigationPosition.Start]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Top]: '',
      [NavigationPosition.End]: '',
    },
    expandableNavVariant: {
      [ExpandableNavigationVariant.XS]: '',
      [ExpandableNavigationVariant.SM]: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: 'items-center gap-y-sm',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: 'justify-center',
      [NavigationItemLabelVariantEnum.None]: 'justify-center',
      // 9px gap puts the label 36px from the item edge (Figma: 8px pad + 20px icon box + 8px gap;
      // our svg is the 18px glyph at 9px pad, so 9+18+9 lands on the same 36px label start).
      [NavigationItemLabelVariantEnum.Platform]:
        'gap-x-lg px-[9px] py-[9px] rounded-md items-center',
    },
    expanded: {
      true: '',
      false: '',
    },
    resolution: {
      [NavigationResolution.LG]: 'disable-hover',
      [NavigationResolution.MD]: 'disable-hover',
      [NavigationResolution.SM]: 'disable-hover',
      [NavigationResolution.XL]: '',
    },
    disabled: {
      true: 'text-disabled cursor-not-allowed',
      false: '',
    },
    alignment: {
      [NavigationItemAlignment.Center]: '',
      [NavigationItemAlignment.Start]: '',
    },
    direction: {
      [NavigationItemDirection.Row]: '',
      [NavigationItemDirection.Column]: '',
    },
    isHorizontalUpfront: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Lite variant styling (common for both types)
    {
      variant: 'lite',
      active: true,
      className: 'ua-side-nav-item-lite-active',
    },
    {
      variant: 'lite',
      active: false,
      className: 'ua-side-nav-item-lite-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'group/NavItemLinkDefault',
    },
    {
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ps-[9px] pe-[9px] py-[9px]',
    },
    {
      expanded: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      // pe 8 (not 9): the trailing chevron is a full 16px box, so unlike the leading glyph it
      // takes the Figma box-level 8px inset from the item edge.
      className: 'py-[8px] pe-[8px]',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Column,
      className: 'justify-center',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Column,
      className: 'flex-col',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Row,
      className: 'flex-row',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Column,
      position: NavigationPosition.Bottom,
      className: 'flex-col',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      position: NavigationPosition.Top,
      className: 'flex-row',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      position: NavigationPosition.Start,
      direction: NavigationItemDirection.Row,
      className: 'flex-row',
    },
    {
      resolution: NavigationResolution.XL,
      disabled: false,
      className: 'cursor-pointer',
    },
    // Lite variant styling
    {
      variant: 'lite',
      active: true,
      className: 'ua-side-nav-item-lite-active',
    },
    {
      variant: 'rich',
      active: false,
      isHorizontalUpfront: true,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Bottom,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className:
        'group/NavSolidExpandableItemLinkDefault ua-side-nav-solid-expandable-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'gap-x-md',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      position: NavigationPosition.Bottom,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      position: NavigationPosition.Start,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      active: true,
      isHorizontalUpfront: true,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      expanded: false,
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      expanded: true,
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'ua-side-nav-solid-expandable-active',
    },
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'py-md',
    },
    // Stroke-specific padding when active is false and labelvariant is Expandable (56px navbar width)
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      active: false,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[18px]',
    },
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      active: true,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[15px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[15px] border-s-[3px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[13px] border-s-[3px]',
    },
    // Stroke-specific padding when active is true and labelvariant is Expandable (52px navbar width)
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      active: false,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.XS,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[16px]',
    },
    {
      variant: 'rich',
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      active: true,
      position: NavigationPosition.Start,
      expandableNavVariant: ExpandableNavigationVariant.XS,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[13px]',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      expanded: true,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      className: 'me-md rounded-sm',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      className: 'ms-[10px] items-center me-md rounded-md',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'border-s-[3px] py-md',
    },
    {
      variant: 'rich',
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'py-md',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'border-s-[3px]',
    },

    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      className: 'border-s-[3px]',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'my-xs',
    },
    {
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'group/NavItemLinkDefault',
    },
    {
      active: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'group/NavItemLinkDefault',
    },
    {
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'group/NavItemLinkDefault',
    },
    {
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'group/NavItemLinkDefault',
    },
    {
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'group/NavItemLinkDefault',
    },
    {
      active: false,
      resolution: NavigationResolution.XL,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'group/NavSolidExpandableItemLinkDefault',
    },
    // Stroke-specific styles when position is bottom
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      className: 'group/NavItemLinkActive border-t-[3px]',
    },
    // To center align the expandable nav item when expandableNavVariant is XS i.e 52px navbar width
    {
      expanded: false,
      active: false,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[18px]',
    },
    {
      expanded: false,
      active: true,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[15px]',
    },
    {
      expanded: true,
      active: true,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[15px]',
    },
    {
      expanded: true,
      active: false,
      variant: 'rich',
      expandableNavVariant: ExpandableNavigationVariant.XS,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'ps-[18px]',
    },
    // To center align the nav item
    {
      position: NavigationPosition.Bottom,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: 'flex-1 justify-center h-full',
    },
    // Solid-specific styles.Margin given to control
    // hover styles
    {
      expanded: false,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      className: 'ms-[10px] me-md rounded-md',
    },
    {
      expanded: true,
      position: NavigationPosition.Start,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      expandableNavVariant: ExpandableNavigationVariant.SM,
      className: 'ms-[10px] me-md rounded-md',
    },
    // Stroke-specific padding
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      className: 'py-md',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'px-[9px] py-[9px]',
    },
    {
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      className: 'pe-md',
    },
    {
      expanded: false,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      className: 'ps-[9px] pe-[9px] py-[9px]',
    },
    {
      expanded: true,
      labelVariant: NavigationItemLabelVariantEnum.Platform,
      // pe 8 (not 9): the trailing chevron is a full 16px box, so unlike the leading glyph it
      // takes the Figma box-level 8px inset from the item edge.
      className: 'py-[8px] pe-[8px]',
    },
    // Stroke-specific styles when Navbar width 56px(SM variant)
  ],
});

export const appSideNavRootVariants = cva('ua-side-nav h-full transition-[width] border-tertiary', {
  variants: {
    expanded: {
      // width comes from the expanded+isPlatformSideNav compounds below (280 vs 282)
      true: '',
      false: '',
    },
    isLabelExpandable: {
      true: '',
      false: '',
    },
    isTopPositioned: {
      true: 'gap-3xl pt-lg',
      false: 'gap-md pt-xl',
    },
    isPlatformSideNav: {
      true: 'border-secondary',
      false: 'absolute start-0 top-0 z-navbar',
    },
  },
  compoundVariants: [
    {
      expanded: true,
      isPlatformSideNav: false,
      className: EXPANDED_SIDE_NAV_WIDTH,
    },
    {
      expanded: true,
      isPlatformSideNav: true,
      // pt drops 16→6 while the header logo grows 32→44 and AppInfo's pb drops 8→6, keeping the
      // header block at 56px in both rail states so the nav items below don't jump on toggle.
      className: `${EXPANDED_PLATFORM_SIDE_NAV_WIDTH} pt-[6px]`,
    },
    {
      expanded: false,
      isLabelExpandable: true,
      isPlatformSideNav: false,
      className: 'w-sidebarWidth',
    },
    {
      expanded: false,
      isPlatformSideNav: true,
      className: 'w-platformSidebarWidth',
    },
    {
      isTopPositioned: false,
      className: 'border-e',
    },
    {
      expanded: true,
      isPlatformSideNav: false,
      className: 'shadow-sm',
    },
    // platform nav is in-flow so expanding pushes content; z-platform-navbar beats route
    // z-overlay panes without moving the rail into modal/listbox layers.
    {
      isPlatformSideNav: true,
      className: 'relative z-platform-navbar gap-y-0',
    },
  ],
});

export const navIconContainerVariants = cva('flex shrink-0', {
  variants: {
    position: {
      [NavigationPosition.Start]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Top]: '',
      [NavigationPosition.End]: '',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: 'justify-center',
      [NavigationItemLabelVariantEnum.Expandable]: 'justify-center',
      [NavigationItemLabelVariantEnum.Tooltip]: 'justify-center',
      [NavigationItemLabelVariantEnum.None]: 'justify-center',
      [NavigationItemLabelVariantEnum.Platform]: 'justify-start',
    },
    variant: {
      lite: '',
      rich: '',
    },
    active: {
      true: '',
      false: '',
    },
    resolution: {
      [NavigationResolution.LG]: 'disable-hover',
      [NavigationResolution.MD]: 'disable-hover',
      [NavigationResolution.SM]: 'disable-hover',
      [NavigationResolution.XL]: '',
    },
    direction: {
      [NavigationItemDirection.Column]: '',
      [NavigationItemDirection.Row]: '',
    },
    alignment: {
      [NavigationItemAlignment.Center]: '',
      [NavigationItemAlignment.Start]: '',
    },
    isHorizontalUpfront: {
      true: '',
      false: '',
    },
    isSubNavItem: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'lite',
      // no trailing margin — the row's gap-x-md is the single source of icon→label spacing,
      // matching the chevron→label gap so every lite gap is md
      className: 'size-[18px] items-center justify-center',
    },
    {
      variant: 'rich',
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Row,
      className: 'flex-1',
    },
    // Solid-specific-styling
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'rounded-md',
    },
    // Applying 'ua-side-nav-item-active-false' for non-expandable
    // solid variants
    {
      variant: 'rich',
      active: false,
      isSubNavItem: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      isSubNavItem: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.None,
      className: 'ua-side-nav-item-active-false',
    },
    {
      variant: 'rich',
      active: false,
      isSubNavItem: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      isHorizontalUpfront: false,
      className: 'ua-side-nav-item-active-false',
    },
    // rich-variant styling. When navitem is active and selectionVariant is stroke,
    // due to 3px border-left, label shifts. To prevent this , adding this translate class
    {
      variant: 'rich',
      active: true,
      isSubNavItem: false,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-x-[1.5px]',
    },
    // rich-variant styling. When navitem is active and selectionVariant is stroke,
    // due to 3px border-top, label shifts. To prevent this , adding this translate class
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px]',
    },
    {
      variant: 'rich',
      active: true,
      position: NavigationPosition.Bottom,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
      className: '-translate-y-[1.5px]',
    },
  ],
  defaultVariants: {
    selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
    labelVariant: NavigationItemLabelVariantEnum.Expandable,
    isSubNavItem: false,
  },
});

export const navItemContainerVariants = cva('', {
  variants: {
    variant: {
      lite: '',
      rich: '',
    },
    position: {
      [NavigationPosition.Start]: '',
      // To allow container to take full available space
      [NavigationPosition.Bottom]: 'flex flex-1 items-center justify-center',
      [NavigationPosition.Top]: 'flex',
      [NavigationPosition.End]: '',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: '',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: '',
      [NavigationItemLabelVariantEnum.None]: '',
      [NavigationItemLabelVariantEnum.Platform]: '',
    },
    direction: {
      [NavigationItemDirection.Column]: '',
      [NavigationItemDirection.Row]: '',
    },
    alignment: {
      [NavigationItemAlignment.Center]: '',
      [NavigationItemAlignment.Start]: '',
    },
    active: {
      true: '',
      false: '',
    },
    isHorizontalUpfront: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'rich',
      active: false,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'group/NavItemLinkDefault',
    },
    {
      variant: 'rich',
      active: false,
      isHorizontalUpfront: true,
      className: 'ua-side-nav-item-active-false',
    },
    // To allow container to take full available space
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'flex flex-1 items-center justify-center',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'flex flex-1 items-center justify-center',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'flex flex-1 items-center justify-center',
    },
    {
      variant: 'rich',
      active: true,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      direction: NavigationItemDirection.Row,
      className: 'ua-side-nav-item-active',
    },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      direction: NavigationItemDirection.Column,
      alignment: NavigationItemAlignment.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'flex flex-col justify-start',
    },
    // {
    //   variant: 'rich',
    //   position: NavigationPosition.Start,
    //   direction: NavigationItemDirection.Column,
    //   alignment: NavigationItemAlignment.Center,
    //   labelVariant: NavigationItemLabelVariantEnum.Upfront,
    //   className: 'flex flex-col items-center justify-center',
    // },
    {
      variant: 'rich',
      position: NavigationPosition.Start,
      direction: NavigationItemDirection.Row,
      alignment: NavigationItemAlignment.Start,
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      className: 'flex flex-row items-start !justify-start',
    },
    // {
    //   variant: 'rich',
    //   position: NavigationPosition.Start,
    //   direction: NavigationItemDirection.Row,
    //   alignment: NavigationItemAlignment.Center,
    //   labelVariant: NavigationItemLabelVariantEnum.Upfront,
    //   className: 'flex flex-row items-center !justify-start',
    // },
  ],
});

export const navItemIconRootClassName = cva('group', {
  variants: {
    hasIcon: {
      true: 'has-icon',
      false: 'has-no-icon',
    },
  },
});

export const userNavItemVariants = cva('', {
  variants: {
    active: {
      true: 'bg-secondary_subtle',
      false: '',
    },
    labelVariant: {
      [NavigationItemLabelVariantEnum.Upfront]: 'justify-center',
      [NavigationItemLabelVariantEnum.Expandable]: '',
      [NavigationItemLabelVariantEnum.Tooltip]: 'justify-center',
      [NavigationItemLabelVariantEnum.None]: 'justify-center',
      [NavigationItemLabelVariantEnum.Platform]: 'justify-start',
    },
    selectionVariant: {
      [NavigationItemSelectionVariantEnum.Stroke]: '',
      [NavigationItemSelectionVariantEnum.Solid]: '',
    },
    isHorizontalUpfront: {
      true: 'flex-start w-full',
      false: '',
    },
    position: {
      [NavigationPosition.Top]: '',
      [NavigationPosition.Bottom]: '',
      [NavigationPosition.Start]: '',
      [NavigationPosition.End]: '',
    },
  },
  compoundVariants: [
    {
      isHorizontalUpfront: false,
      active: false,
      position: NavigationPosition.Start,
      className: 'ua-side-nav-item-active-false',
    },
    {
      labelVariant: NavigationItemLabelVariantEnum.Expandable,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'mt-md',
    },
    {
      labelVariant: NavigationItemLabelVariantEnum.None,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'mt-sm',
    },
    {
      labelVariant: NavigationItemLabelVariantEnum.Tooltip,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'mt-sm',
    },
    {
      labelVariant: NavigationItemLabelVariantEnum.Upfront,
      selectionVariant: NavigationItemSelectionVariantEnum.Solid,
      className: 'mt-sm',
    },
  ],
});
